import type { ChartSection, ChartVariant, PhrasingMode } from '../chart/types.ts';

// `ChartVariant` → DOM. Ten moduł nie wie nic o Ultimate Guitar ani o
// ChordSheetJS, jedynie o kształcie `ChartVariant`. Granica względem
// `src/chart/` jest świadoma: kompilator jest czystą funkcją i testuje się
// bez przeglądarki, ten moduł testuje się TYLKO z przeglądarką (Playwright),
// dlatego nie ma dla niego osobnych testów jednostkowych: prawdziwym testem
// jest test wysokości w `e2e/`.
//
// Wygląd, kierunek "ŻAR": śpiewnik czytany przy ogniu. Akordy są bohaterem
// ekranu i dostają wąski, mocny krój, kotwica tekstowa dostaje szeryfową
// kursywę, bo jest CYTATEM ze śpiewanego tekstu i ma się odróżniać rodzajem
// pisma, nie samą wielkością. Metadane i mapa układu są przyklejone do
// krawędzi, sekcje rozkładają się w tym, co zostanie.
//
// Pierwsza wersja renderowała wszystko krojem stałopozycyjnym, przejętym z
// sondy `tools/probe.html`. Zmiana nie jest samą estetyką: krój wąski mieści
// WIĘCEJ znaków w wierszu, więc przy tej samej treści pozwala na WIĘKSZY
// stopień pisma, a to jest dokładnie waluta tego projektu. Kroje są
// systemowe na iOS, więc nie pobiera się ani jednego bajtu i praca bez
// zasięgu zostaje prawdziwa.
//
// CAŁA treść zostaje w normalnym przepływie dokumentu, żadnego
// `position: fixed` ani `overflow: hidden`, bo inaczej test wysokości w e2e
// mierzyłby coś innego niż to, co naprawdę widać na ekranie.

export interface RenderOptions {
  fontSizePx: number;
  /** Gdy true, dołącza dyskretny znacznik "skrót skrócony" (poziom zdegradowany, poniżej progu). */
  showTruncatedBadge?: boolean;
  /** Gdy true, dopisuje listę tego, co degradacja ukryła. DOMYŚLNIE FAŁSZ i to
   * jest decyzja produktowa, nie oszczędność miejsca: „ukryto: frazy w
   * osobnych wierszach, znaki podziału fraz..." to diagnostyka kompilatora,
   * czyli tekst dla mnie, a nie dla grającego. Na ekranie, którego cała
   * obietnica brzmi „tylko to, co potrzebne do zagrania", zajmowała trzy
   * wiersze w najdroższym miejscu urządzenia. Zostaje dostępna dla podglądu
   * technicznego (`?diag=1`) i dla testów. */
  showDroppedInfo?: boolean;
  /** Gdy true, wiersz akordów nie ma prawa się zawinąć i nadmiar w poziomie
   * oblewa pomiar. Gdy false, zawinięcie jest dozwolone, ale WYŁĄCZNIE na
   * granicy frazy, a kontynuacja dostaje wcięcie wiszące. Wyboru dokonuje
   * `dopasuj()`, nie renderer: to decyzja o polityce, nie o rysowaniu. */
  nowrapChords?: boolean;
  /**
   * Gdy true, wiersz metadanych (`.og-meta`) renderuje się ZAWSZE, nawet gdy
   * utwór nie ma żadnych metadanych do pokazania. To jest włącznik widoku
   * grania w prawdziwej aplikacji (`src/main.ts`), gdzie wiersz jest CELEM
   * DOTKNIĘCIA panelu stroju (`src/ui/stroj.ts`, etap 7 planu) i musi istnieć
   * zawsze, żeby wejście było osiągalne niezależnie od treści utworu.
   *
   * Domyślnie (harness testu wysokości `e2e/harness.ts` i sondy w `e2e/`)
   * zostaje WYŁĄCZONY, świadomie: te testy mierzą samą geometrię kompilatora
   * i renderera na korpusie, nie znają panelu stroju i nie mają powodu
   * dokładać wiersza, którego nikt tam nie dotyka. Progi degradacji dla
   * korpusu są już skalibrowane BEZ tego wiersza (patrz `fitLoop.ts`), więc
   * włączanie go tam bez potrzeby przesunęłoby te progi po cichu.
   */
  wierszMetaZawsze?: boolean;
  /** Stan stroju (transpozycja/kapodaster gracza), dopisywany do wiersza
   * metadanych, gdy `wierszMetaZawsze` jest włączone. `null`, gdy strój jest
   * domyślny (patrz `formatujStanStroju` w `src/ui/stroj.ts` - "zero milczy"). */
  strojInfo?: string | null;
}

const STYLE_ID = 'ognisko-chart-style';
const ROOT_CLASS = 'ognisko-chart';

/** Wstrzykuje arkusz stylów raz na dokument (idempotentnie), żeby wielokrotne
 * wywołania `renderChart` (np. przy zmianie wariantu) nie mnożyły `<style>`. */
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.${ROOT_CLASS} {
  --og-zar: var(--zar-zar, #ff7a2f);
  --og-kosc: var(--zar-kosc, #f6efe4);
  --og-popiol: var(--zar-popiol, #9a8d7d);
  --og-cien: var(--zar-cien, #2a2018);
  --og-waski: var(--zar-waski, "Avenir Next Condensed", "Futura", "Helvetica Neue", sans-serif);
  --og-szeryf: var(--zar-szeryf, "Iowan Old Style", Palatino, Charter, Georgia, serif);
  font-family: var(--og-waski);
  font-variant-ligatures: none;
  /* 1,16 było dobrane przy zamiennikach krojów na Linuksie, gdzie stosunek
     wysokości znaku do kegla wynosi 1,15. Kroje systemowe iOS mają go w
     okolicach 1,3, więc przy 1,16 znaki sąsiednich wierszy stykały się tuszem,
     mimo że układ formalnie się mieścił. */
  line-height: 1.24;
  color: var(--og-kosc);
  padding: 0.35em 0.5em 0.2em;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.${ROOT_CLASS} * { box-sizing: border-box; }

/* Wiersz metadanych: tonacja, kapodaster, metrum. Mały, rozstrzelony, w
   kolorze żaru, żeby czytało się jak podpis, a nie jak treść. */
.${ROOT_CLASS} .og-meta {
  flex: 0 0 auto;
  font-size: 0.42em;
  font-weight: 600;
  /* Bez wersalików: tonacja „Ab” (As) czytała się jako „AB”, czyli jak dwa
     akordy, a „Bb” jak „BB”. Wielkość litery w nazwie akordu niesie treść. */
  letter-spacing: 0.08em;
  color: var(--og-zar);
  opacity: 0.85;
  margin-bottom: 0.5em;
}

/* Sekcje dostają cały zapas miejsca i rozkładają się w nim równomiernie.
   To jest sedno odpowiedzi na zarzut "zajmuje kawałek ekranu": skrót nie ma
   kulić się u góry, tylko wypełniać ekran, bo każdy wolny piksel to większy
   akord czytany z dwóch metrów. */
.${ROOT_CLASS} .og-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-height: 0;
}
/* Wolne miejsce rozdają MIĘDZY SIEBIE same sekcje (\`flex-grow\`), a nie
   \`justify-content: space-evenly\` na rodzicu. Różnica jest zasadnicza dla
   pomiaru, nie dla wyglądu: przy wyśrodkowanym rozkładzie nadmiar treści
   wylewa się w OBIE strony, a nadmiar nad górną krawędzią NIE wchodzi do
   \`scrollHeight\`, więc "mieści się" odpowiadało twierdząco, gdy ostatnia
   kotwica wchodziła już na pasek układu. Przy rozpychaniu sekcji nadmiar
   idzie wyłącznie w dół i pomiar go widzi. */
/* Sekcja rośnie razem z wolnym miejscem, ale jej TREŚĆ trzyma się kupy i stoi
   w środku własnego pola. Pionowy słupek żaru, który wcześniej biegł przez całą
   sekcję, zniknął: rósł razem z pustką, więc jego długość niosła informację,
   której nie ma (dłuższy słupek nie znaczy dłuższej sekcji, tylko więcej
   wolnego miejsca pod nią). Zamiast niego jest krótka kreska przy etykiecie i
   włosowa linia między sekcjami, czyli podział, który mówi prawdę. */
.${ROOT_CLASS} .og-sec {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.16em 0;
  transition: background 0.3s ease, border-color 0.3s ease;
}
.${ROOT_CLASS} .og-sec + .og-sec {
  border-top: 1px solid transparent;
  border-image: linear-gradient(90deg, rgba(255,122,47,0.12), rgba(246,239,228,0.04) 50%, transparent) 1;
}

/* Reczne sledzenie pozycji (Faza 1): gitarzysta dotyka brzegu ekranu, zeby
   przesunac podswietlenie na sasiednia sekcje. Subtelne, bez migania: lekkie
   tlo i kreska w kolorze zaru, zaden ruch ani animacja. */
.${ROOT_CLASS} .og-sec--aktywna {
  background: linear-gradient(90deg, rgba(255,122,47,0.10) 0%, rgba(255,122,47,0.03) 100%);
  border-left: 2px solid var(--og-zar);
  padding-left: 0.3em;
  box-shadow: inset 4px 0 12px rgba(255,122,47,0.06);
}
.${ROOT_CLASS} .og-sec--aktywna .og-lbl {
  color: var(--og-zar);
  opacity: 1;
}

/* Etykieta sekcji: znacznik, nie nagłówek. Ma się dać znaleźć wzrokiem w
   ułamku sekundy i nie zabierać miejsca akordom. */
.${ROOT_CLASS} .og-lbl {
  color: var(--og-zar);
  font-size: 0.4em;
  letter-spacing: 0.18em;
  font-weight: 700;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 0.5em;
  margin-bottom: 0.12em;
}
.${ROOT_CLASS} .og-lbl::before {
  content: "";
  width: 1.1em;
  height: 2px;
  border-radius: 2px;
  flex: 0 0 auto;
  background: linear-gradient(90deg, var(--og-zar), rgba(255,122,47,0.15));
}

/* Akordy: bohater ekranu. Wąski krój daje więcej znaków w wierszu, więc
   pozwala na WIĘKSZY stopień pisma przy tej samej treści, a nie tylko
   wygląda inaczej. Delikatna poświata odkleja je od ciemnego tła. */
.${ROOT_CLASS} .og-ch {
  color: var(--og-kosc);
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0 0.7em rgba(255,150,80,0.18);
  /* Wcięcie wiszące: kontynuacja zawiniętego wiersza cofa się pod frazę, więc
     widać, że to dalszy ciąg, a nie nowa fraza. Na zrzucie z telefonu MH
     sierota "Am Em Am" stała równo z lewą krawędzią i czytała się jak osobna
     grupa akordów, czyli wyglądała na informację, której tam nie ma. */
  text-indent: -0.9em;
  padding-left: 0.9em;
}
.${ROOT_CLASS}.og-nowrap .og-ch { white-space: nowrap; text-indent: 0; padding-left: 0; }
/* Fraza jest nierozrywalna, więc jedyne miejsca, w których wiersz akordów
   może się złamać, to granice fraz. Bez tego łamał się w środku frazy. */
.${ROOT_CLASS} .og-ph { white-space: nowrap; }
.${ROOT_CLASS} .og-akord { white-space: nowrap; }
.${ROOT_CLASS} .og-dop { font-size: 0.74em; font-weight: 600; letter-spacing: 0; opacity: 0.92; }
.${ROOT_CLASS} .og-sep { color: var(--og-zar); opacity: 0.55; }
/* Licznik powtórzeń i ogon to NIE są akordy, tylko instrukcja, ile razy grać.
   Kolor żaru odróżnia je od nazw akordów szybciej niż jakikolwiek odstęp. */
.${ROOT_CLASS} .og-mult { color: var(--og-zar); font-weight: 700; }

/* Kotwica tekstowa to CYTAT ze śpiewanego tekstu, więc dostaje inny głos:
   szeryfowa kursywa w kolorze popiołu. Odróżnia się od akordów rodzajem
   pisma, nie samą wielkością, więc nie myli się z nimi nawet kątem oka. */
.${ROOT_CLASS} .og-cue {
  font-family: var(--og-szeryf);
  font-style: italic;
  font-weight: 400;
  letter-spacing: 0;
  color: var(--og-popiol);
  font-size: 0.5em;
  margin-top: 0.06em;
}
.${ROOT_CLASS} .og-tabonly {
  font-family: var(--og-szeryf);
  font-style: italic;
  color: var(--og-popiol);
  font-size: 0.46em;
  opacity: 0.8;
}

/* Odsyłacz "jak Z1" (poziom 7, między rodzajami): kolor popiołu, NIE żaru.
   Powód jest funkcjonalny: żaru używają akordy, a odsyłacz akordem nie jest i
   nie ma prawa dać się przeczytać jako nazwa akordu przy ognisku. */
.${ROOT_CLASS} .og-ref {
  color: var(--og-popiol);
  white-space: nowrap;
}

/* Mapa układu: pasek orientacyjny przyklejony do dołu ekranu. */
.${ROOT_CLASS} .og-map {
  flex: 0 0 auto;
  margin-top: 0.45em;
  padding-top: 0.35em;
  border-top: 1px solid var(--og-cien);
  color: rgba(246,239,228,0.5);
  font-size: 0.38em;
  letter-spacing: 0.08em;
  white-space: pre-wrap;
}
.${ROOT_CLASS} .og-notes {
  flex: 0 0 auto;
  font-family: var(--og-szeryf);
  margin-top: 0.3em;
  color: var(--og-popiol);
  opacity: 0.75;
  font-size: 0.36em;
  line-height: 1.35;
  white-space: pre-line;
}
.${ROOT_CLASS} .og-dropped {
  flex: 0 0 auto;
  margin-top: 0.3em;
  color: var(--og-popiol);
  opacity: 0.6;
  font-size: 0.34em;
}
.og-mic {
  position: fixed;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  right: 12px;
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(154,141,125,0.3);
  background: rgba(20, 16, 13, 0.85);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--og-popiol);
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}
.og-mic:active {
  transform: scale(0.92);
}
.og-mic--aktywny {
  border-color: var(--og-zar);
  color: var(--og-zar);
  background: rgba(255, 122, 47, 0.12);
  box-shadow: 0 0 16px rgba(255,122,47,0.2);
  animation: og-mic-pulse 2s ease-in-out infinite;
}
@keyframes og-mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,122,47,0.3); }
  50% { box-shadow: 0 0 0 8px rgba(255,122,47,0); }
}
`;
  document.head.appendChild(style);
}

/**
 * Znak granicy frazy dla trybu `separators` (patrz `PhrasingMode` w
 * `src/chart/types.ts`). Wybrany środkowy kropek U+00B7 (MIDDLE DOT), a nie
 * np. `|`, dlatego że:
 *
 * 1. `|` jest w tym projekcie ZAREZERWOWANY na granicę taktu (niezmiennik
 *    "żaden akord nie zawiera kreski pionowej", patrz `TAB_REMNANT_RE` i
 *    notacja taktowa w `preNormalize.ts`) i na granicę bloku w `layoutMap`
 *    (`compile.ts`, `layoutMap()`). Użycie go też jako granicy frazy
 *    kolidowałoby z obydwoma znaczeniami na tym samym ekranie.
 * 2. Środkowy kropek nie występuje w gramatyce nazw akordów tego projektu:
 *    `Chord.parse` (ChordSheetJS) i lokalne wzorce (`progression.ts`,
 *    `preNormalize.ts`) budują nazwy akordów wyłącznie z liter A-G, `#`, `b`,
 *    cyfr, `/` (bas), nawiasów i przyrostków łacińskich (`m`, `sus`, `add`,
 *    `dim`, `aug`) - nigdy z U+00B7. Sprawdzone testem w `chart.test.ts`
 *    (`normalizeChordToken` nie rozpoznaje separatora jako akordu).
 * 3. U+00B7 leży w Latin-1 Supplement, obecnym w komplecie krojów z listy
 *    czcionek renderera, więc nie zależy od fallbacku emoji ani od tabeli
 *    glifów spoza podstawowego zestawu.
 *
 *    Poprzednia wersja tego punktu dodawała, że "w monospace zajmuje dokładnie
 *    jedną kolumnę znaku". Renderer nie używa już kroju stałopozycyjnego, więc
 *    to uzasadnienie przestało obowiązywać i zostało zdjęte, a nie przepisane:
 *    argument o dostępności glifu jest prawdziwy dalej, argument o szerokości
 *    kolumny nie. Sam wybór znaku pozostaje słuszny z powodów 1 i 2, które od
 *    kroju nie zależą.
 *
 * Otoczony spacjami (nie sklejony z akordami), żeby przeglądarka mogła
 * zawinąć wiersz na naturalnej granicy słowa, tak jak zawija zwykłe spacje
 * między akordami - koszt to jeden dodatkowy znak na granicę frazy, zgodnie
 * z zadaniem ("mniej więcej jeden znak na frazę"), nie cały wiersz.
 */
export const PHRASE_SEPARATOR = ' · ';

// Etykieta nad sekcją pełnym słowem. Kompilator daje skróty (Z1, R, Most),
// które zostają w mapie układu na dole ekranu, bo tam liczy się szerokość.
// Nad sekcją etykieta ma własny wiersz drobnym pismem, więc pełne słowo nie
// kosztuje ani piksela wysokości, a czyta się bez znajomości skrótów.
const NAZWY_SEKCJI: Record<string, string> = {
  Z: 'Zwrotka', R: 'Refren', P: 'Przedrefren', O: 'Zakończenie', I: 'Wstęp',
  Int: 'Przerywnik', Instr: 'Instrumentalnie', Most: 'Most', Solo: 'Solo',
};
const ETYKIETA_RE = /^(Instr|Int|Most|Solo|Z|R|P|O|I)(\d*)$/;

export function etykietaSekcji(etykiety: readonly string[]): string {
  const czesci = etykiety.map((e) => {
    const m = ETYKIETA_RE.exec(e);
    return m ? { nazwa: NAZWY_SEKCJI[m[1]!]!, nr: m[2]! } : { nazwa: e, nr: '' };
  });
  if (new Set(etykiety).size === 1) {
    const c = czesci[0]!;
    return c.nr ? `${c.nazwa} ${c.nr}` : c.nazwa;
  }
  // Scalone sekcje jednego rodzaju („Z1 Z2”) czytają się jako „Zwrotka 1, 2”.
  const jednaNazwa = czesci.every((c) => c.nazwa === czesci[0]!.nazwa && c.nr !== '');
  if (czesci.length > 1 && jednaNazwa) return `${czesci[0]!.nazwa} ${czesci.map((c) => c.nr).join(', ')}`;
  return czesci.map((c) => (c.nr ? `${c.nazwa} ${c.nr}` : c.nazwa)).join(' · ');
}

/**
 * Renderuje progresję akordów sekcji wg `mode` (własność WARIANTU, nie
 * poziomu - patrz `PhrasingMode`):
 * - `lines`: jedna fraza (linia źródłowa taba) na wiersz, tak jak gitarzysta
 *   widział je w oryginalnym tabie - odpowiedź na zarzut "siedemnaście
 *   akordów w jednym ciągu, nie widać gdzie kończy się fraza";
 * - `separators`: frazy w jednym wierszu, rozdzielone `PHRASE_SEPARATOR`;
 * - `flat`: dotychczasowy jeden zbiorczy wiersz `base ×times + tail`, bez
 *   żadnego śladu granic fraz - wtedy liczy się oszczędność miejsca, nie
 *   czytelność frazowania.
 *
 * Gdy `progression.phrases` jest puste (sekcja bez własnych akordów, poza
 * przypadkiem `tabOnly` obsłużonym osobno przez wywołującego), `lines` i
 * `separators` spadają na to samo, co `flat`, a przy pustej bazie nie
 * renderuje się nic.
 */
function appendProgression(sec: HTMLElement, section: ChartSection, mode: PhrasingMode): void {
  if (mode === 'lines' && section.progression.phrases.length > 0) {
    for (const phrase of section.progression.phrases) {
      const wiersz = document.createElement('div');
      wiersz.className = 'og-ch';
      wstawAkordy(wiersz, phrase);
      sec.appendChild(wiersz);
    }
    return;
  }
  if (mode === 'separators' && section.progression.phrases.length > 0) {
    const wiersz = document.createElement('div');
    wiersz.className = 'og-ch';
    section.progression.phrases.forEach((phrase, i) => {
      if (i > 0) {
        // Separator w OSOBNYM elemencie. Przed nim spacja nierozdzielająca, za
        // nim zwykła: wiersz łamie się po kropce, więc kropka zamyka frazę na
        // końcu wiersza, a następny wiersz zaczyna się od akordu. Przy dwóch
        // zwykłych spacjach kontynuacja potrafiła zaczynać się od „·”.
        // Kolor żaru odróżnia znak podziału od akordów.
        wiersz.appendChild(document.createTextNode('\u00a0'));
        const sep = document.createElement('span');
        sep.className = 'og-sep';
        sep.textContent = PHRASE_SEPARATOR.trim();
        wiersz.appendChild(sep);
        wiersz.appendChild(document.createTextNode(' '));
      }
      const el = document.createElement('span');
      el.className = 'og-ph';
      wstawAkordy(el, phrase);
      wiersz.appendChild(el);
    });
    sec.appendChild(wiersz);
    return;
  }
  const { progression } = section;
  if (progression.base.length === 0) return;
  const wiersz = document.createElement('div');
  wiersz.className = 'og-ch';
  wstawAkordy(wiersz, progression.base);
  if (progression.times > 1) {
    wiersz.appendChild(document.createTextNode(' '));
    const mnoznik = document.createElement('span');
    mnoznik.className = 'og-mult';
    mnoznik.textContent = `×${progression.times}`;
    wiersz.appendChild(mnoznik);
  }
  if (progression.tail.length > 0) {
    const plus = document.createElement('span');
    plus.className = 'og-mult';
    plus.textContent = ' + ';
    wiersz.appendChild(plus);
    wstawAkordy(wiersz, progression.tail);
  }
  sec.appendChild(wiersz);
}

// Rdzeń akordu (A, F#, Bb) pełnym stopniem, dopisek (m7, sus2, maj7, /F#)
// mniejszym, jak w drukowanych śpiewnikach. Po rdzeniu łapie się chwyt jednym
// spojrzeniem, a węższy ciąg akordów oddaje miejsce na większe pismo. Tekst
// elementu zostaje ten sam co przy zwykłym `join(' ')`.
const RDZEN_AKORDU_RE = /^([A-H](?:#|b)?)(.*)$/;

function wstawAkordy(cel: HTMLElement, akordy: readonly string[]): void {
  akordy.forEach((akord, i) => {
    if (i > 0) cel.appendChild(document.createTextNode(' '));
    const m = RDZEN_AKORDU_RE.exec(akord);
    if (!m || m[2] === '') {
      cel.appendChild(document.createTextNode(akord));
      return;
    }
    const el = document.createElement('span');
    el.className = 'og-akord';
    el.appendChild(document.createTextNode(m[1]!));
    const dopisek = document.createElement('span');
    dopisek.className = 'og-dop';
    dopisek.textContent = m[2]!;
    el.appendChild(dopisek);
    cel.appendChild(el);
  });
}

function formatMetaLine(variant: ChartVariant, strojInfo: string | null): string | null {
  const parts: string[] = [];
  if (variant.meta.key !== null) parts.push(variant.meta.key);
  if (variant.meta.capo !== null) parts.push(`capo ${variant.meta.capo}`);
  if (variant.meta.time !== null) parts.push(variant.meta.time);
  if (variant.meta.tempo !== null) parts.push(`≈${variant.meta.tempo} bpm`);
  if (strojInfo !== null) parts.push(strojInfo);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function appendLine(parent: HTMLElement, className: string, text: string): void {
  const el = document.createElement('div');
  el.className = className;
  el.textContent = text;
  parent.appendChild(el);
}

/**
 * Renderuje `variant` do wnętrza `container`, czyszcząc jego dotychczasową
 * zawartość. Czysto imperatywne budowanie DOM, bez frameworka: aplikacja jest
 * mała, a dodatkowa zależność nie jest warta wagi na urządzeniu offline.
 */
export function renderChart(container: HTMLElement, variant: ChartVariant, options: RenderOptions): void {
  ensureStyle();

  container.replaceChildren();
  container.classList.add(ROOT_CLASS);
  container.classList.toggle('og-nowrap', options.nowrapChords === true);
  container.style.fontSize = `${options.fontSizePx}px`;

  const metaLine = formatMetaLine(variant, options.strojInfo ?? null);
  if (metaLine !== null) {
    appendLine(container, 'og-meta', metaLine);
  } else if (options.wierszMetaZawsze === true) {
    // Utwór bez żadnych metadanych i bez ustawionego stroju: wiersz i tak ma
    // się pojawić, jako stały cel dotknięcia panelu stroju (patrz
    // `RenderOptions.wierszMetaZawsze`). Spacja nierozdzielająca zamiast
    // pustego stringa, żeby wiersz miał realną, mierzalną wysokość linii -
    // pusty `textContent` nie tworzy węzła tekstowego i wiersz skurczyłby się
    // do zera, czyli przestał być czymkolwiek, co da się dotknąć.
    appendLine(container, 'og-meta', ' ');
  }

  // Sekcje mieszkają we wspólnym elemencie rozciągliwym, żeby wolne miejsce
  // rozłożyło się MIĘDZY nie, zamiast zostać jedną pustą połową ekranu pod
  // spodem. Wiersz metadanych i mapa układu zostają przyklejone do krawędzi.
  const body = document.createElement('div');
  body.className = 'og-body';
  container.appendChild(body);

  for (const section of variant.sections) {
    const sec = document.createElement('div');
    sec.className = 'og-sec';

    appendLine(sec, 'og-lbl', etykietaSekcji(section.labels));

    const referenceTarget = variant.references[section.id];
    if (section.tabOnly) {
      appendLine(sec, 'og-tabonly', 'partia tabulaturowa');
    } else if (referenceTarget !== undefined) {
      appendLine(sec, 'og-ref', `jak ${referenceTarget}`);
    } else {
      appendProgression(sec, section, variant.phrasingMode);
    }

    if (section.anchor !== null) appendLine(sec, 'og-cue', section.anchor);

    body.appendChild(sec);
  }

  // Znacznik "skrót skrócony" dopisuje się do wiersza mapy układu, a NIE
  // tworzy własnego elementu. Powód jest ten sam, dla którego pasek podglądu
  // technicznego powstaje przed dopasowaniem: element dokładany dopiero przy
  // renderze finalnym nie brał udziału w żadnym pomiarze, więc treść
  // przelewała się dokładnie o jego wysokość. Trafiało to w jedyną ścieżkę,
  // na której zapasu z definicji nie ma, bo status "zdegradowany" znaczy, że
  // nic nie zmieściło się przy progu.
  let mapLine = `Układ  ${variant.layoutMap}`;
  if (variant.meta.strumming !== null) mapLine += ` · bicie ${variant.meta.strumming}`;
  if (options.showTruncatedBadge === true) mapLine += ' · skrót skrócony';
  appendLine(container, 'og-map', mapLine);

  if (options.showDroppedInfo === true && variant.droppedInfo.length > 0) {
    appendLine(container, 'og-dropped', `ukryto: ${variant.droppedInfo.join(', ')}`);
  }

  // Uwagi końcowe autora (np. "zagraj jeszcze raz refren, potem intro/outro,
  // zakończ na Em"), pokazane dyskretnie na samym dole, poza mapą układu:
  // to jest dosłowna proza autora, NIE część mapy formalnych nagłówków.
  if (variant.meta.notes !== null) {
    appendLine(container, 'og-notes', variant.meta.notes);
  }
}

/**
 * Reczne sledzenie pozycji (Faza 1): zdejmuje `.og-sec--aktywna` ze
 * wszystkich sekcji w `container`, po czym - jesli `indeks` nie jest `null` -
 * dodaje ja n-tej sekcji (licząc od 0) wewnątrz `.og-body`. Wywoluj po
 * kazdym renderze i po kazdym dotknieciu brzegu ekranu w widoku grania.
 */
export function podswietlSekcje(container: HTMLElement, indeks: number | null): void {
  const sekcje = container.querySelectorAll<HTMLElement>('.og-body .og-sec');
  sekcje.forEach((sekcja) => sekcja.classList.remove('og-sec--aktywna'));
  if (indeks === null) return;
  const cel = sekcje[indeks];
  cel?.classList.add('og-sec--aktywna');
}
